import instanceAxios from '../config/api'
import {
  AnalyzeProofResponse,
  MaturityApiErrorBody,
  MaturityScoring,
  MaturityScoringResponse,
} from '../types/maturityScoringTypes'

const API_URL = '/maturityScorings'

/** AI proof analysis can take many minutes on large PDFs — do not abort early. */
const ANALYZE_PROOF_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

function isSoftError(data: unknown): data is MaturityApiErrorBody {
  if (!data || typeof data !== 'object') return false
  const body = data as MaturityApiErrorBody
  return body.error === true || body.erreur === true
}

function assertMaturitySuccess<T>(data: T): T {
  if (isSoftError(data)) {
    throw new Error(data.message || 'Maturity scoring request failed.')
  }
  return data
}

export const getAllMaturityScorings = async ({
  page,
  pageSize,
  search,
}: {
  page?: number
  pageSize?: number
  search?: string
}) => {
  try {
    const response = await instanceAxios.get<MaturityScoringResponse>(
      `${API_URL}`,
      {
        params: {
          page,
          pageSize,
          search,
        },
      }
    )
    return assertMaturitySuccess(response.data)
  } catch (error) {
    throw new Error('Failed to fetch maturityScoring.')
  }
}

export const deleteMaturityScoringApi = async (MaturityScoringId: string) => {
  try {
    const response = await instanceAxios.delete(`${API_URL}/` + MaturityScoringId)
    return assertMaturitySuccess(response.data)
  } catch (error) {
    throw new Error('Failed to fetch maturityScorings.')
  }
}

export const addMaturityScoringApi = async (
  formData: FormData
): Promise<MaturityScoring> => {
  try {
    const response = await instanceAxios.post(`${API_URL}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return assertMaturitySuccess(response.data) as MaturityScoring
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to add MaturityScoring'
    throw new Error(message)
  }
}

export const updateMaturityScoring = async (
  id: string,
  data: FormData
): Promise<MaturityScoring> => {
  try {
    const response = await instanceAxios.put(`${API_URL}/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return assertMaturitySuccess(response.data) as MaturityScoring
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update maturity scoring.'
    throw new Error(message)
  }
}

export const getMaturityScoringbyIdApi = async (MaturityScoringId: string) => {
  try {
    const response = await instanceAxios.get<MaturityScoring>(
      `${API_URL}/` + MaturityScoringId
    )
    return assertMaturitySuccess(response.data)
  } catch (error) {
    throw new Error('Failed to fetch maturityScoring.')
  }
}

export const getMaturityScoringBySubDomainIdApi = async (
  SubDomainId: string
) => {
  try {
    const response = await instanceAxios.get<MaturityScoring>(
      `/subDomains/getMaturityScoring/` + SubDomainId
    )
    return assertMaturitySuccess(response.data)
  } catch (error) {
    throw new Error('Failed to fetch maturityScoring.')
  }
}

/**
 * AI analysis of a maturity proof document.
 * Match working backend clients: FormData + Bearer only (no manual Content-Type).
 */
export const analyzeProofApi = async (input: {
  proof: File
  SubDomainId: string
  signal?: AbortSignal
}): Promise<AnalyzeProofResponse> => {
  if (!(input.proof instanceof Blob)) {
    throw new Error('Proof file is required')
  }
  const subDomainId = String(input.SubDomainId || '').trim()
  if (!subDomainId) {
    throw new Error('SubDomainId is required')
  }

  const formData = new FormData()
  // Explicit filename helps multer/FileInterceptor detect the upload
  formData.append('proof', input.proof, input.proof.name || 'proof.bin')
  formData.append('SubDomainId', subDomainId)

  const token = localStorage.getItem('token')

  try {
    const response = await instanceAxios.post<AnalyzeProofResponse>(
      `${API_URL}/analyze-proof`,
      formData,
      {
        timeout: ANALYZE_PROOF_TIMEOUT_MS,
        signal: input.signal,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Prevent axios from trying to JSON-encode FormData
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData && headers) {
              // Let the browser set multipart boundary
              delete (headers as Record<string, unknown>)['Content-Type']
              delete (headers as Record<string, unknown>)['content-type']
            }
            return data
          },
        ],
      }
    )

    const data = response.data as AnalyzeProofResponse
    // Success payload always includes suggestedScore
    if (data && typeof data.suggestedScore === 'number') {
      return data
    }
    // Soft-error body (HTTP 200 + { error: true, message })
    if (isSoftError(data)) {
      throw new Error(data.message || 'Maturity scoring request failed.')
    }
    throw new Error('Invalid AI response')
  } catch (error: unknown) {
    const axiosError = error as {
      code?: string
      message?: string
      response?: { data?: { message?: string; error?: string; statusCode?: number } }
    }

    if (
      axiosError?.code === 'ERR_CANCELED' ||
      (error instanceof Error && error.name === 'CanceledError')
    ) {
      throw error
    }

    if (
      !axiosError?.response &&
      (axiosError?.code === 'ERR_NETWORK' || axiosError?.message === 'Network Error')
    ) {
      throw new Error('ANALYSIS_UNREACHABLE')
    }

    if (axiosError?.code === 'ECONNABORTED') {
      throw new Error(
        'Analysis timed out after 30 minutes. The document may be too large — please try a smaller file or try again.'
      )
    }

    // Soft-error / validation Error thrown above (no HTTP response)
    if (error instanceof Error && axiosError?.response == null && !axiosError?.code) {
      throw error
    }

    const status = axiosError?.response?.data?.statusCode
    const serverMessage =
      axiosError?.response?.data?.message ||
      axiosError?.response?.data?.error

    if (
      status === 500 ||
      (typeof serverMessage === 'string' &&
        /internal server error/i.test(serverMessage))
    ) {
      throw new Error(
        'AI analysis service is unavailable. Start the Compliance AI service (port 8002), then try again.'
      )
    }

    throw new Error(
      (typeof serverMessage === 'string' && serverMessage) ||
        axiosError?.message ||
        'Failed to analyze proof.'
    )
  }
}
